
import { motion } from 'framer-motion';
import { ArrowRight, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button, PageMotion } from '@/shared/ui';
import { AssigneeIcon, CatalogIcon, NotificationsIcon } from '@/shared/config/entity-icons';
import { cn } from '@/shared/lib/cn';
import { LoginButton, useAuthStore } from '@/features/auth';
import styles from './Home.module.css';

const STOPS: Array<{ Icon: typeof CatalogIcon; title: string; text: string }> = [
  { Icon: CatalogIcon, title: 'Выберите услугу', text: 'Каталог всего, с чем помогает поддержка' },
  { Icon: AssigneeIcon, title: 'Заявка найдёт инженера', text: 'Сразу уходит группе, которая этим занимается' },
  { Icon: NotificationsIcon, title: 'Ход работы на виду', text: 'Ответы и смена статуса приходят уведомлением' },
];

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

const rise = (delay: number, duration = 0.5) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration, delay, ease: EASE_OUT },
});

const appear = (delay: number) => ({
  initial: { opacity: 0, scale: 0.85 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.48, delay, ease: EASE_OUT },
});

const JOURNEY_START = 0.28;
const JOURNEY_STEP = 0.56;
const SEG_LAG = 0.2;
const SEG_DURATION = 0.64;

const stopDelay = (i: number) => JOURNEY_START + i * JOURNEY_STEP;
const segStyle = (i: number) =>
  ({
    '--seg-delay': `${stopDelay(i) + SEG_LAG}s`,
    '--seg-duration': `${SEG_DURATION}s`,
  }) as React.CSSProperties;

function CtaIcon({ Icon }: { Icon: typeof ArrowRight }) {
  return (
    <span className={styles.ctaIcon} aria-hidden>
      <Icon size={16} />
    </span>
  );
}

export function HomePage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return (
    <PageMotion className={cn(styles.page, !isAuthenticated && styles.pageGuest)}>
      <section className={styles.hero}>
        <motion.h1 className={styles.title} {...rise(0)}>
          { }
          Pridex <span className={styles.titleAccent}>Support</span>
        </motion.h1>
        <motion.p className={styles.subtitle} {...rise(0.08)}>
          { }
          Заявка, переписка и состояние работы&nbsp;— в одном месте
        </motion.p>
        <motion.div
          className={cn(styles.actions, isAuthenticated && styles.actionsPair)}
          {...rise(0.16)}
        >
          { }
          {isAuthenticated ? (
            <>
              <Link to="/tickets">
                <Button size="lg" className={styles.cta}>
                  Мои заявки
                  <CtaIcon Icon={ArrowRight} />
                </Button>
              </Link>
              <Link to="/categories">
                <Button size="lg" variant="secondary" className={cn(styles.cta, styles.ctaSoft)}>
                  Новое обращение
                  <CtaIcon Icon={Plus} />
                </Button>
              </Link>
            </>
          ) : (
            <LoginButton className={styles.cta}>
              Войти
              <CtaIcon Icon={ArrowRight} />
            </LoginButton>
          )}
        </motion.div>
      </section>

      <section className={styles.journeySection} aria-labelledby="home-journey">
        <h2 id="home-journey" className={styles.srOnly}>
          Как это устроено
        </h2>
        { }
        <ol className={styles.journey}>
          {STOPS.map(({ Icon, title, text }, i) => (
            <li key={title} className={styles.stop} style={segStyle(i)}>
              <motion.span className={styles.stopDot} aria-hidden {...appear(stopDelay(i))}>
                <Icon size={20} />
              </motion.span>
              <motion.span className={styles.stopTitle} {...rise(stopDelay(i) + 0.08, 0.48)}>
                {title}
              </motion.span>
              <motion.p className={styles.stopText} {...rise(stopDelay(i) + 0.14, 0.48)}>
                {text}
              </motion.p>
            </li>
          ))}
        </ol>
      </section>
    </PageMotion>
  );
}
